import type { ZodError } from "zod";
import { BaseError, type BaseErrorSubClassPropsType } from "./base.ts";

/**
 * An error thrown when an unknown error occurs.
 *
 * @example
 * throw new UnknownError("An unknown error occurred");
 */
export const UnknownError = BaseError.subclass("UnknownError");

export type NativeErrorProps = {
  platform: "browser" | "node" | "react-native" | "ios" | "android";
};
/**
 * An error thrown when an error native to a particular platform occurs.
 *
 * @example
 * throw new NativeError("A native error occurred");
 */
export const NativeError = BaseError.subclass("NativeError", {
  props: {} as NativeErrorProps,
});

export type InputErrorProps = BaseErrorSubClassPropsType<{
  expected: unknown;
  actual: unknown;
}>;
/**
 * An error thrown when a user input is invalid.
 *
 * @example
 * throw new InputError("Input is invalid", {
 *   props: { expected: "string", actual: 123 },
 * });
 */
export const InputError = BaseError.subclass("InputError", {
  props: {} as InputErrorProps,
});

export type ValidationErrorProps = BaseErrorSubClassPropsType<{
  expected: unknown;
  actual: unknown;
}>;
/**
 * An error thrown when a validation fails.
 *
 * @example
 * throw new ValidationError("Validation failed", {
 *   props: { expected: "string", actual: 123 },
 * });
 */
export const ValidationError = BaseError.subclass("ValidationError", {
  props: {} as ValidationErrorProps,
});

export type SchemaValidationErrorProps<T> = BaseErrorSubClassPropsType<{
  parseError: ZodError<T>;
}>;
/**
 * An error thrown when a Zod schema validation fails.
 *
 * @example
 * const zodParseResult = SomeZodSchema.safeParse(params);
 * throw new SchemaValidationError("Failed to validate provided data with schema.", {
 *   props: { parseError: zodParseResult.error },
 * }); */
export const SchemaValidationError = BaseError.subclass(
  "SchemaValidationError",
  {
    props: {} as SchemaValidationErrorProps<unknown>,
  }
);

/**
 * An error thrown when an invariant fails.
 *
 * @example
 * throw new InvariantError("Invariant failed");
 */
export const InvariantError = BaseError.subclass("InvariantError");

export type NonExistentPropertyErrorProps = BaseErrorSubClassPropsType<{
  propertyName: string;
}>;
/**
 * An error thrown when a property does not exist.
 *
 * @example
 * throw new NonExistentPropertyError("Property 'name' does not exist", {
 *   props: { propertyName: "name" },
 * });
 */
export const NonExistentPropertyError = BaseError.subclass(
  "NonExistentPropertyError",
  {
    props: {} as NonExistentPropertyErrorProps,
  }
);

export type DuplicatePropertyErrorProps = BaseErrorSubClassPropsType<{
  propertyName: string;
}>;
/**
 * An error thrown when a property is duplicated.
 *
 * @example
 * throw new DuplicatePropertyError("Property 'name' is duplicated", {
 *   props: { propertyName: "name" },
 * });
 */
export const DuplicatePropertyError = BaseError.subclass(
  "DuplicatePropertyError",
  {
    props: {} as DuplicatePropertyErrorProps,
  }
);

/**
 * An error thrown when a reference is invalid.
 *
 * @example
 * throw new InvalidReferenceError("Question 1 references question 2, but question 2 does not exist");
 */
export const InvalidReferenceError = BaseError.subclass(
  "InvalidReferenceError"
);

export type KeyValueStorageErrorProps = BaseErrorSubClassPropsType<{
  key: string;
  value: unknown;
  storageSystem: "localStorage" | "sessionStorage" | "cookie";
}>;
/**
 * An error thrown when a key-value storage operation fails.
 *
 * @example
 * throw new KeyValueStorageError("Key-value storage operation failed", {
 *   props: { key: "name", value: "John", storageSystem: "localStorage" },
 * });
 */
export const KeyValueStorageError = BaseError.subclass("KeyValueStorageError", {
  props: {} as KeyValueStorageErrorProps,
});

export type MutationErrorProps = BaseErrorSubClassPropsType<{
  expected: unknown;
  actual: unknown;
}>;
/**
 * An error thrown when a mutation fails.
 *
 * @example
 * throw new MutationError("Mutation failed", {
 *   props: { expected: "string", actual: 123 },
 * });
 */
export const MutationError = BaseError.subclass("MutationError", {
  props: {} as MutationErrorProps,
});

export type ExportErrorProps = BaseErrorSubClassPropsType<{
  fileMimeType: string;
}>;
/**
 * An error thrown when a file export fails.
 *
 * @example
 * throw new ExportError("File export failed", {
 *   props: { fileMimeType: "application/pdf" },
 * });
 */
export const ExportError = BaseError.subclass("ExportError", {
  props: {} as ExportErrorProps,
});

/**
 * An error thrown when a user is not authenticated.
 */
export const AuthenticationError = BaseError.subclass("AuthenticationError");

/**
 * An error thrown when a user is not authorized to access a resource.
 *
 * @example
 * throw new AuthorizationError("User is not authorized to access this resource");
 */
export const AuthorizationError = BaseError.subclass("AuthorizationError");
